import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import LandingPagesAdmin from "@/components/LandingPagesAdmin";
import LandingStatsPanel from "@/components/LandingStatsPanel";
import MetaConnectPanel from "@/components/MetaConnectPanel";
import {
  EnvelopeSimple,
  Phone,
  Buildings,
  ArrowsClockwise,
  Users,
  CreditCard,
  Sparkle,
  Copy,
  Broom,
  SignIn,
  Globe,
  VideoCamera,
  Trash,
  Warning,
} from "@phosphor-icons/react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const STATUS_STYLE = {
  new: "bg-brand/10 text-brand",
  contacted: "bg-blue-100 text-blue-700",
  won: "bg-emerald-100 text-emerald-700",
  lost: "bg-neutral-200 text-neutral-600",
};
const STATUS_LABEL = {
  new: "Ny",
  contacted: "Kontaktad",
  won: "Vunnen",
  lost: "Förlorad",
};
const STATUS_OPTIONS = ["new", "contacted", "won", "lost"];

const SUB_STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  trialing: "bg-blue-100 text-blue-700",
  active: "bg-emerald-100 text-emerald-700",
  past_due: "bg-red-100 text-red-700",
  unpaid: "bg-red-100 text-red-700",
  canceled: "bg-neutral-200 text-neutral-600",
  grandfathered: "bg-purple-100 text-purple-700",
};
const SUB_STATUS_LABEL = {
  pending: "Väntar kort",
  trialing: "Prövotid",
  active: "Aktiv",
  past_due: "Betalning fallerar",
  unpaid: "Obetald",
  canceled: "Uppsagd",
  grandfathered: "Permanent",
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("sv-SE", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function fmtDateShort(iso) {
  try {
    return new Date(iso).toLocaleDateString("sv-SE", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function AdminInquiries() {
  const { user } = useAuth();
  const [tab, setTab] = useState(() => {
    // Efter OAuth-callback landar användaren på ?tab=meta
    if (typeof window !== "undefined") {
      const q = new URLSearchParams(window.location.search);
      if (q.get("tab") === "meta" || q.get("meta_connected") || q.get("meta_error")) {
        return "meta";
      }
    }
    return "inquiries";
  }); // "inquiries" | "signups" | "demos" | "webhooks" | "landing" | "meta"
  const [items, setItems] = useState([]);
  const [signups, setSignups] = useState([]);
  const [demos, setDemos] = useState([]);
  const [demoPassword, setDemoPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [signupsLoading, setSignupsLoading] = useState(false);
  const [demosLoading, setDemosLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [sandboxPreview, setSandboxPreview] = useState(null);
  const [sandboxLoading, setSandboxLoading] = useState(false);
  const [sandboxCleaning, setSandboxCleaning] = useState(false);
  const [webhookDiag, setWebhookDiag] = useState(null);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookEndpoints, setWebhookEndpoints] = useState(null);
  const [endpointsLoading, setEndpointsLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  // Delete/purge state
  const [deleteTarget, setDeleteTarget] = useState(null); // {organization_id, name}
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [purgePreview, setPurgePreview] = useState(null); // {candidates:[], count}
  const [purgeDays, setPurgeDays] = useState(30);
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeApplying, setPurgeApplying] = useState(false);

  const loadInquiries = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/leads/integration-inquiries`, {
        withCredentials: true,
      });
      setItems(res.data?.items || []);
    } catch {
      toast.error("Kunde inte hämta förfrågningar");
    } finally {
      setLoading(false);
    }
  };

  const loadSignups = async () => {
    setSignupsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/leads/signups?limit=200`, {
        withCredentials: true,
      });
      setSignups(res.data?.items || []);
    } catch {
      toast.error("Kunde inte hämta signups");
    } finally {
      setSignupsLoading(false);
    }
  };

  const loadDemos = async () => {
    setDemosLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/internal/seed-demos`, {
        withCredentials: true,
      });
      setDemos(res.data?.demos || []);
      setDemoPassword(res.data?.shared_password || "");
    } catch {
      toast.error("Kunde inte hämta demo-orgar");
    } finally {
      setDemosLoading(false);
    }
  };

  const regenerateDemos = async () => {
    if (!window.confirm(
      "Detta rensar och återskapar alla 4 demo-organisationer med ny dummy-data. Fortsätta?"
    )) return;
    setSeeding(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/internal/seed-demos`,
        {},
        { withCredentials: true }
      );
      toast.success(`Demo-orgar återskapade (${res.data?.seeded?.length || 0} st)`);
      await loadDemos();
    } catch {
      toast.error("Kunde inte återskapa demo-orgar");
    } finally {
      setSeeding(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} kopierad`),
      () => toast.error("Kunde inte kopiera")
    );
  };

  const loadSandboxPreview = async () => {
    setSandboxLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/internal/sandbox-cleanup`, {
        withCredentials: true,
      });
      setSandboxPreview(res.data);
    } catch {
      toast.error("Kunde inte ladda sandbox-preview");
    } finally {
      setSandboxLoading(false);
    }
  };

  const applySandboxCleanup = async () => {  if (!sandboxPreview || sandboxPreview.matched_count === 0) return;
    if (!window.confirm(
      `Detta migrerar ${sandboxPreview.matched_count} sandbox-org(ar) till "grandfathered" och nollställer deras subscription_id. Fortsätta?`
    )) return;
    setSandboxCleaning(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/internal/sandbox-cleanup`,
        {},
        { withCredentials: true }
      );
      toast.success(`${res.data?.modified_count || 0} orgar migrerade till grandfathered`);
      await Promise.all([loadSandboxPreview(), loadSignups()]);
    } catch {
      toast.error("Kunde inte köra sandbox-städ");
    } finally {
      setSandboxCleaning(false);
    }
  };

  const deleteSingleOrg = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmText.trim() !== (deleteTarget.name || "").trim()) {
      toast.error("Namnet matchar inte");
      return;
    }
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${API_BASE}/api/leads/signups/${encodeURIComponent(deleteTarget.organization_id)}`,
        {
          params: { confirm_name: deleteTarget.name },
          withCredentials: true,
        }
      );
      const total = Object.values(res.data?.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`${deleteTarget.name} raderad (${total} dokument borta)`);
      setDeleteTarget(null);
      setDeleteConfirmText("");
      await loadSignups();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte radera");
    } finally {
      setDeleting(false);
    }
  };

  const loadPurgePreview = async () => {
    setPurgeLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/leads/signups/purge-old-trials`,
        { older_than_days: purgeDays, dry_run: true },
        { withCredentials: true }
      );
      setPurgePreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte hämta preview");
    } finally {
      setPurgeLoading(false);
    }
  };

  const applyPurge = async () => {
    if (!purgePreview || purgePreview.count === 0) return;
    if (!window.confirm(
      `Radera ${purgePreview.count} avbrutna trial-orgar PERMANENT? Detta går inte att ångra.`
    )) return;
    setPurgeApplying(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/leads/signups/purge-old-trials`,
        { older_than_days: purgeDays, dry_run: false },
        { withCredentials: true }
      );
      toast.success(`${res.data?.count || 0} orgar raderade`);
      setPurgePreview(null);
      await loadSignups();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte köra rensning");
    } finally {
      setPurgeApplying(false);
    }
  };

  const loadWebhookDiag = async () => {
    setWebhookLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/stripe/webhook-diagnostics?limit=25`, {
        withCredentials: true,
      });
      setWebhookDiag(res.data);
    } catch {
      toast.error("Kunde inte hämta webhook-diagnostik");
    } finally {
      setWebhookLoading(false);
    }
  };

  const loadWebhookEndpoints = async () => {
    setEndpointsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/stripe/webhook-endpoints`, {
        withCredentials: true,
      });
      setWebhookEndpoints(res.data);
    } catch {
      toast.error("Kunde inte hämta Stripe webhook-endpoints");
    } finally {
      setEndpointsLoading(false);
    }
  };

  useEffect(() => {
    loadInquiries();
    loadSignups();
    loadDemos();
    loadSandboxPreview();
    loadWebhookDiag();
  }, []);

  // Redirect om inte super_admin (belt-and-suspenders — backend nekar också).
  if (user && !user.is_super_admin) {
    return <Navigate to="/" replace />;
  }

  const updateStatus = async (inquiry_id, status) => {
    setSavingId(inquiry_id);
    try {
      await axios.patch(
        `${API_BASE}/api/leads/integration-inquiries/${inquiry_id}`,
        { status },
        { withCredentials: true }
      );
      setItems((it) => it.map((i) => (i.inquiry_id === inquiry_id ? { ...i, status } : i)));
      toast.success(`Status: ${STATUS_LABEL[status]}`);
    } catch {
      toast.error("Kunde inte uppdatera status");
    } finally {
      setSavingId(null);
    }
  };

  const inquiryCounts = items.reduce((acc, i) => {
    acc[i.status] = (acc[i.status] || 0) + 1;
    return acc;
  }, {});

  const signupCounts = signups.reduce((acc, s) => {
    acc[s.subscription_status] = (acc[s.subscription_status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="admin-inquiries-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight" data-testid="admin-inquiries-title">
            Leads
          </h1>
          <p className="text-sm text-text-muted mt-1">
            Pipeline: publika integrationsförfrågningar + nya signups.
          </p>
        </div>
        <button
          onClick={
            tab === "inquiries" ? loadInquiries : tab === "signups" ? loadSignups : loadDemos
          }
          className="border border-app hover:bg-surface-hover rounded-md px-3 py-2 text-sm inline-flex items-center gap-2"
          data-testid="admin-inquiries-refresh"
        >
          <ArrowsClockwise size={14} weight="bold" />
          Uppdatera
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-app mb-6" data-testid="admin-leads-tabs">
        <button
          onClick={() => setTab("inquiries")}
          data-testid="tab-inquiries"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "inquiries"
              ? "border-brand text-brand"
              : "border-transparent text-text-muted hover:text-text-primary"
          }`}
        >
          Förfrågningar <span className="ml-1 opacity-70 tabular-nums">({items.length})</span>
        </button>
        <button
          onClick={() => setTab("signups")}
          data-testid="tab-signups"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "signups"
              ? "border-brand text-brand"
              : "border-transparent text-text-muted hover:text-text-primary"
          }`}
        >
          Signups <span className="ml-1 opacity-70 tabular-nums">({signups.length})</span>
        </button>
        <button
          onClick={() => setTab("demos")}
          data-testid="tab-demos"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "demos"
              ? "border-brand text-brand"
              : "border-transparent text-text-muted hover:text-text-primary"
          }`}
        >
          Demos <span className="ml-1 opacity-70 tabular-nums">({demos.filter(d => d.exists).length}/{demos.length || 4})</span>
        </button>
        <button
          onClick={() => { setTab("webhooks"); loadWebhookDiag(); }}
          data-testid="tab-webhooks"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "webhooks"
              ? "border-brand text-brand"
              : "border-transparent text-text-muted hover:text-text-primary"
          }`}
        >
          Stripe Webhooks
          {webhookDiag?.totals?.signature_failed > 0 && (
            <span className="ml-1 inline-block px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-semibold">
              {webhookDiag.totals.signature_failed} fel
            </span>
          )}
        </button>
        <button
          onClick={() => setTab("landing")}
          data-testid="tab-landing"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
            tab === "landing"
              ? "border-brand text-brand"
              : "border-transparent text-text-muted hover:text-text-primary"
          }`}
        >
          <Globe size={14} weight="bold" />
          Landningssidor
        </button>
        <button
          onClick={() => setTab("meta")}
          data-testid="tab-meta"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
            tab === "meta"
              ? "border-brand text-brand"
              : "border-transparent text-text-muted hover:text-text-primary"
          }`}
        >
          <VideoCamera size={14} weight="bold" />
          Meta / Reels
        </button>
      </div>

      {tab === "inquiries" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {STATUS_OPTIONS.map((s) => (
              <div
                key={s}
                className="bg-surface border border-app rounded-lg px-4 py-3"
                data-testid={`admin-inquiries-count-${s}`}
              >
                <div className="text-xs text-text-muted">{STATUS_LABEL[s]}</div>
                <div className="font-heading text-2xl">{inquiryCounts[s] || 0}</div>
              </div>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-16 text-text-muted">Laddar…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 bg-surface border border-app rounded-lg">
              <div className="text-text-muted">Inga förfrågningar än — dela länken /integrationer.</div>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((i) => (
                <InquiryCard
                  key={i.inquiry_id}
                  inquiry={i}
                  savingId={savingId}
                  onUpdateStatus={updateStatus}
                />
              ))}
            </div>
          )}
        </>
      )}

      {tab === "signups" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {["pending", "trialing", "active", "past_due", "canceled"].map((s) => (
              <div
                key={s}
                className="bg-surface border border-app rounded-lg px-4 py-3"
                data-testid={`admin-signups-count-${s}`}
              >
                <div className="text-xs text-text-muted">{SUB_STATUS_LABEL[s]}</div>
                <div className="font-heading text-2xl">{signupCounts[s] || 0}</div>
              </div>
            ))}
          </div>

          <SandboxCleanupPanel
            preview={sandboxPreview}
            loading={sandboxLoading}
            cleaning={sandboxCleaning}
            onReload={loadSandboxPreview}
            onApply={applySandboxCleanup}
          />

          <PurgeTrialsPanel
            days={purgeDays}
            onDaysChange={setPurgeDays}
            preview={purgePreview}
            loading={purgeLoading}
            applying={purgeApplying}
            onLoadPreview={loadPurgePreview}
            onApply={applyPurge}
            onCancel={() => setPurgePreview(null)}
          />

          {signupsLoading ? (
            <div className="text-center py-16 text-text-muted">Laddar…</div>
          ) : signups.length === 0 ? (
            <div className="text-center py-16 bg-surface border border-app rounded-lg">
              <div className="text-text-muted">Inga signups ännu.</div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="admin-signups-list">
              {signups.map((s) => (
                <SignupCard
                  key={s.organization_id}
                  signup={s}
                  ownOrgId={user?.organization_id}
                  onDelete={() => { setDeleteTarget(s); setDeleteConfirmText(""); }}
                />
              ))}
            </div>
          )}
        </>
      )}
      {tab === "demos" && (
        <div data-testid="admin-demos-panel">
          <div className="bg-surface border border-app rounded-xl p-5 mb-6 flex flex-wrap items-start justify-between gap-4">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 mb-1">
                <Sparkle size={18} weight="fill" className="text-brand" />
                <h2 className="font-heading text-lg">Demo-organisationer</h2>
              </div>
              <p className="text-sm text-text-muted leading-relaxed">
                4 permanenta demo-orgar med rik svensk dummy-data för sälj-demos.
                Alla har status <strong>grandfathered</strong> (fri åtkomst, ingen Stripe).
                Delat lösenord för alla konton:{" "}
                <code className="bg-app border border-app rounded px-1.5 py-0.5 text-xs">
                  {demoPassword || "Demo1234!"}
                </code>
              </p>
            </div>
            <button
              onClick={regenerateDemos}
              disabled={seeding}
              className="bg-brand text-white hover:opacity-90 disabled:opacity-50 rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 whitespace-nowrap"
              data-testid="admin-demos-regenerate"
            >
              <ArrowsClockwise size={14} weight="bold" className={seeding ? "animate-spin" : ""} />
              {seeding ? "Skapar…" : "Rensa och återskapa"}
            </button>
          </div>

          {demosLoading ? (
            <div className="text-center py-16 text-text-muted">Laddar…</div>
          ) : demos.length === 0 ? (
            <div className="text-center py-16 bg-surface border border-app rounded-lg">
              <div className="text-text-muted">
                Inga demo-orgar än. Klicka &quot;Rensa och återskapa&quot; för att skapa dem.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-demos-list">
              {demos.map((d) => (
                <DemoCard
                  key={d.organization_id}
                  demo={d}
                  password={demoPassword}
                  onCopy={copy}
                />
              ))}
            </div>
          )}
        </div>
      )}
      {tab === "webhooks" && (
        <div data-testid="admin-webhooks-panel">
          <div className="bg-surface border border-app rounded-xl p-5 mb-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-[260px]">
                <h2 className="font-heading text-lg mb-1">Stripe Webhook-diagnostik</h2>
                <p className="text-sm text-text-muted leading-relaxed">
                  Visar om Stripe faktiskt når backenden och om signatur / processering
                  fungerar. Använd detta när checkout &quot;fastnar&quot; på Kontroll X/15.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    if (!window.confirm("Radera alla historiska signature_failed-rader? (OK-rader behålls)")) return;
                    try {
                      const res = await axios.delete(`${API_BASE}/api/stripe/webhook-logs?only_failed=true`, { withCredentials: true });
                      toast.success(`${res.data.deleted} gamla FAILED-rader raderade`);
                      loadWebhookDiag();
                    } catch {
                      toast.error("Kunde inte rensa loggar");
                    }
                  }}
                  className="border border-app hover:bg-surface-hover rounded-md px-3 py-2 text-sm inline-flex items-center gap-2 text-red-700"
                  data-testid="admin-webhooks-clear-failed"
                >
                  Rensa gamla FAILED
                </button>
                <button
                  onClick={loadWebhookDiag}
                  disabled={webhookLoading}
                  className="border border-app hover:bg-surface-hover rounded-md px-3 py-2 text-sm inline-flex items-center gap-2"
                  data-testid="admin-webhooks-refresh"
                >
                  <ArrowsClockwise size={14} weight="bold" className={webhookLoading ? "animate-spin" : ""} />
                  Uppdatera
                </button>
              </div>
            </div>

            {webhookDiag && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-app border border-app rounded p-3">
                  <div className="text-text-muted text-xs uppercase tracking-widest">Mode</div>
                  <div className="font-mono mt-1">{webhookDiag.stripe_mode || "—"}</div>
                </div>
                <div className="bg-app border border-app rounded p-3">
                  <div className="text-text-muted text-xs uppercase tracking-widest">Secret källa</div>
                  <div className="font-mono mt-1">
                    {webhookDiag.webhook_secret_configured
                      ? webhookDiag.webhook_secret_source || "—"
                      : <span className="text-red-600">Saknas!</span>}
                  </div>
                </div>
                <div className="bg-app border border-app rounded p-3">
                  <div className="text-text-muted text-xs uppercase tracking-widest">Totalt mottagna</div>
                  <div className="font-mono mt-1">{webhookDiag.totals?.all_time ?? 0}</div>
                </div>
                <div className="bg-app border border-app rounded p-3">
                  <div className="text-text-muted text-xs uppercase tracking-widest">Signatur-fel</div>
                  <div className={"font-mono mt-1 " + ((webhookDiag.totals?.signature_failed || 0) > 0 ? "text-red-600 font-semibold" : "")}>
                    {webhookDiag.totals?.signature_failed ?? 0}
                  </div>
                </div>
                <div className="bg-app border border-app rounded p-3 col-span-2 md:col-span-4">
                  <div className="text-text-muted text-xs uppercase tracking-widest">Aktivt fingeravtryck (jämför med Stripe Dashboard → Webhooks → Signing Secret)</div>
                  <div className="font-mono mt-1 text-sm break-all" data-testid="webhook-secret-fingerprint">
                    {webhookDiag.webhook_secret_fingerprint || "—"}
                  </div>
                  {webhookDiag.byok_and_managed_differ && (
                    <div className="mt-2 text-xs text-red-600" data-testid="byok-vs-managed-mismatch">
                      ⚠ BYOK och MANAGED secrets skiljer sig åt — BYOK ({webhookDiag.webhook_secret_byok_fingerprint}) används,
                      MANAGED ({webhookDiag.webhook_secret_managed_fingerprint}) ignoreras. Om Stripe Dashboard visar MANAGED
                      fingerprint är detta orsaken till signatur-fel.
                    </div>
                  )}
                </div>
              </div>
            )}

            {webhookDiag && webhookDiag.totals?.all_time === 0 && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
                <strong>Inga webhook-anrop mottagna än.</strong> Om ni har gjort en checkout
                och detta står 0 → Stripe når inte backenden. Verifiera webhook-URL i
                Stripe Dashboard: skall vara <code className="font-mono text-xs">https://app.trappkonsulten.se/api/stripe/webhook</code>
              </div>
            )}

            {/* Endpoint-listning från Stripe API för att upptäcka duplikat */}
            <div className="mt-6 border-t border-app pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-sm">Registrerade webhook-endpoints i Stripe</h3>
                  <p className="text-xs text-text-muted">
                    Om det finns fler än 1 endpoint kan duplikat vara orsak till signatur-fel.
                    Stripe visar bara URL — signing secret måste bekräftas via Dashboard.
                  </p>
                </div>
                <button
                  onClick={loadWebhookEndpoints}
                  disabled={endpointsLoading}
                  className="border border-app rounded px-3 py-1 text-xs hover:bg-app disabled:opacity-50"
                  data-testid="load-webhook-endpoints-btn"
                >
                  {endpointsLoading ? "Hämtar…" : (webhookEndpoints ? "Uppdatera" : "Hämta lista")}
                </button>
              </div>

              {webhookEndpoints?.error && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
                  {webhookEndpoints.error}
                </div>
              )}

              {webhookEndpoints?.endpoints?.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="webhook-endpoints-list">
                  {webhookEndpoints.endpoints.map((ep) => (
                    <div
                      key={ep.id}
                      className={"border rounded p-3 text-sm " + (ep.matches_our_backend ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50")}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-mono text-xs break-all">{ep.url}</div>
                        <span className={"text-xs px-2 py-0.5 rounded " + (ep.status === "enabled" ? "bg-emerald-200 text-emerald-900" : "bg-slate-200")}>
                          {ep.status}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-text-muted flex flex-wrap gap-x-4">
                        <span>ID: <code className="font-mono">{ep.id}</code></span>
                        <span>API: {ep.api_version || "default"}</span>
                        <span>Events: {ep.enabled_events_count}</span>
                        <span>{ep.livemode ? "LIVE" : "TEST"}</span>
                      </div>
                      {!ep.matches_our_backend && (
                        <div className="mt-2 text-xs text-red-700">
                          ⚠ Denna endpoint pekar INTE på vår backend. Radera i Stripe Dashboard
                          om den inte används av annat system.
                        </div>
                      )}
                    </div>
                  ))}
                  {webhookEndpoints.count > 1 && (
                    <div className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-2">
                      <strong>Notera:</strong> {webhookEndpoints.count} endpoints registrerade.
                      Om flera pekar på samma URL har vardera egen signing secret — Stripe
                      Dashboard visar en; vår `.env` kanske har en annan. Behåll endast EN
                      endpoint för `/api/stripe/webhook`.
                    </div>
                  )}
                </div>
              )}
            </div>

            {(() => {
              // Kolla senaste 25 events för att avgöra AKTUELL hälsa
              // (istället för att bara stirra på all-time totals som aldrig sjunker)
              const recent = webhookDiag?.recent || [];
              const recentFailed = recent.filter(w => w.status === "signature_failed").length;
              const recentOk = recent.filter(w => w.status === "signature_ok").length;
              const totalFailed = webhookDiag?.totals?.signature_failed || 0;
              const hasHistory = webhookDiag?.totals?.all_time > 0;

              if (!hasHistory) return null;

              // Kolla de 5 SENASTE eventen — om alla är OK, är integrationen frisk NU
              // (äldre historiska fel från t.ex. secret-rotation ska inte spamma röd banner)
              const latestFive = recent.slice(0, 5);
              const latestFailed = latestFive.filter(w => w.status === "signature_failed").length;
              const latestOk = latestFive.filter(w => w.status === "signature_ok").length;

              // 🕰️ Tidscheck: om senaste eventet är äldre än 1 timme räknas det som HISTORISKT
              // (annars kan gamla fel från t.ex. bortkopplade sandbox-webhooks få den röda
              // flaggan att stanna kvar för alltid, eftersom "senaste 5" aldrig uppdateras)
              const latestTs = recent[0]?.received_at;
              let ageHours = null;
              if (latestTs) {
                const d = new Date(latestTs);
                if (!isNaN(d.getTime())) {
                  ageHours = (Date.now() - d.getTime()) / 3600000;
                }
              }
              const isStale = ageHours !== null && ageHours > 1;

              // 🟢 GRÖN: senaste 5 alla OK
              if (latestFive.length >= 3 && latestFailed === 0 && latestOk > 0) {
                return (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded p-3 text-sm text-green-900">
                    <strong>Webhook-integrationen fungerar.</strong> Senaste {latestFive.length} anrop har passerat signaturvalidering.
                    {(recentFailed > 0 || totalFailed > recentFailed) && (
                      <span className="block mt-1 text-xs text-green-800">
                        (Äldre signatur-fel visas i statistiken ovan — från tidigare felkonfiguration, påverkar inte drift.)
                      </span>
                    )}
                  </div>
                );
              }

              // 🔵 BLÅ: senaste felen är GAMLA (>1h) och inga nya anrop kommer in
              // — troligen redan löst men historiken ligger kvar. Erbjud städning.
              if (isStale && latestFailed > 0) {
                const ageLabel = ageHours >= 24
                  ? `${Math.floor(ageHours / 24)} d`
                  : `${Math.floor(ageHours)} h`;
                return (
                  <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
                    <strong>Historiska signatur-fel visas.</strong> Senaste webhook-anropet är {ageLabel} gammalt — inga nya fel har kommit in sedan dess.
                    Detta är troligen redan åtgärdat (t.ex. gamla sandbox-webhooks som kopplats bort). Klicka <em>Rensa gamla FAILED</em> ovan för att städa historiken.
                  </div>
                );
              }

              // 🟡 GUL: några av de senaste failar, men inte alla
              if (latestFailed > 0 && latestOk > 0) {
                return (
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
                    <strong>Blandade resultat i senaste anropen ({latestFailed} fel / {latestFive.length}).</strong> Kan vara Stripe-retries av äldre events. Övervaka — om det inte klarnar inom 30 min, kontakta Emergent Support.
                  </div>
                );
              }

              // 🔴 RÖD: senaste anropen failar alla OCH är färska (<1h gamla)
              if (latestFailed > 0) {
                return (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-900">
                    <strong>Signatur-fel i senaste anropen ({latestFailed}/{latestFive.length}).</strong> Det betyder att STRIPE_WEBHOOK_SECRET
                    i Deploy Secrets inte matchar det som finns i Stripe Dashboard →
                    Webhooks → Signing Secret. Kontakta Emergent Support för att synka.
                  </div>
                );
              }

              return null;
            })()}
          </div>

          <div className="bg-surface border border-app rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-app text-sm font-medium">
              Senaste {webhookDiag?.recent?.length || 0} webhook-anrop
            </div>
            {webhookLoading ? (
              <div className="text-center py-16 text-text-muted">Laddar…</div>
            ) : !webhookDiag?.recent?.length ? (
              <div className="text-center py-16 text-text-muted text-sm">
                Inga webhook-anrop loggade än.
              </div>
            ) : (
              <div className="divide-y divide-app text-sm">
                {webhookDiag.recent.map((w, i) => (
                  <div key={i} className="px-4 py-3 flex flex-wrap items-start gap-3">
                    <div className="text-xs text-text-muted whitespace-nowrap font-mono w-40">
                      {new Date(w.received_at).toLocaleString("sv-SE")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="font-mono text-xs bg-app border border-app rounded px-1.5 py-0.5">
                          {w.event_type || "—"}
                        </code>
                        <span className={
                          "text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full whitespace-nowrap " +
                          (w.status === "signature_ok"
                            ? "bg-green-100 text-green-700"
                            : w.status === "signature_failed"
                            ? "bg-red-100 text-red-700"
                            : "bg-amber-100 text-amber-700")
                        }>
                          {w.status}
                        </span>
                        {w.processed && w.processed !== "ok" && (
                          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 whitespace-nowrap">
                            {w.processed}
                          </span>
                        )}
                        {w.org_id && (
                          <span className="text-[10px] font-mono text-text-muted">
                            org={w.org_id}
                          </span>
                        )}
                      </div>
                      {(w.error || w.processing_error) && (
                        <div className="mt-1 text-xs text-red-600 font-mono break-all">
                          {w.error || w.processing_error}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {tab === "landing" && (
        <>
          <LandingStatsPanel />
          <LandingPagesAdmin />
        </>
      )}
      {tab === "meta" && (
        <MetaConnectPanel />
      )}

      {deleteTarget && (
        <div
          data-testid="admin-delete-org-modal"
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div
            className="bg-surface border border-app rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <Warning size={22} className="text-red-600" />
              <h2 className="font-heading text-xl">Radera organisation permanent</h2>
            </div>
            <p className="text-sm text-text-muted mb-2">
              Detta raderar <strong className="text-text-primary">{deleteTarget.name}</strong> och ALL associerad data (användare, objekt, moment, tidrapporter, arbetsdagar, avvikelser osv). Går inte att ångra.
            </p>
            <p className="text-sm text-text-muted mb-4">
              Skriv organisationens namn <code className="bg-app border border-app rounded px-1.5 py-0.5 text-xs">{deleteTarget.name}</code> för att aktivera Radera-knappen.
            </p>
            <input
              type="text"
              autoFocus
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="tk-input w-full mb-4"
              placeholder={deleteTarget.name}
              data-testid="admin-delete-confirm-input"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="tk-btn-outline flex-1"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={deleteSingleOrg}
                disabled={deleting || deleteConfirmText.trim() !== deleteTarget.name.trim()}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-md px-4 py-2 text-sm flex-1 inline-flex items-center justify-center gap-2"
                data-testid="admin-delete-confirm-button"
              >
                <Trash size={14} />
                {deleting ? "Raderar…" : "Radera permanent"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DemoCard({ demo: d, password, onCopy }) {
  const [opening, setOpening] = useState(false);

  const openDemo = async () => {
    setOpening(true);
    try {
      await axios.post(
        `${API_BASE}/api/internal/demo-login/${d.organization_id}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Loggar in som ${d.name}…`);
      // Full reload så AuthContext hämtar nya cookien och layout uppdateras
      window.location.href = "/";
    } catch {
      toast.error("Kunde inte öppna demo — kör 'Rensa och återskapa' först");
      setOpening(false);
    }
  };

  return (
    <div
      className="bg-surface border border-app rounded-xl p-5"
      data-testid={`admin-demo-${d.organization_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-heading text-lg flex items-center gap-2">
            <Buildings size={16} className="text-brand" /> {d.name}
          </div>
          <div className="text-xs text-text-muted mt-1">/{d.slug || "—"}</div>
        </div>
        <span
          className={
            "text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full whitespace-nowrap " +
            (d.exists
              ? "bg-purple-100 text-purple-700"
              : "bg-red-100 text-red-700")
          }
        >
          {d.exists ? d.subscription_status || "OK" : "Saknas"}
        </span>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between bg-app border border-app rounded-md px-3 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <EnvelopeSimple size={14} className="text-text-muted flex-shrink-0" />
            <span className="font-mono text-xs truncate">{d.admin_email}</span>
          </div>
          <button
            onClick={() => onCopy(d.admin_email, "E-post")}
            className="text-text-muted hover:text-brand flex-shrink-0"
            data-testid={`admin-demo-copy-email-${d.organization_id}`}
            title="Kopiera e-post"
          >
            <Copy size={14} />
          </button>
        </div>

        <div className="flex items-center justify-between bg-app border border-app rounded-md px-3 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-text-muted text-xs">Lösenord</span>
            <span className="font-mono text-xs">{password || "Demo1234!"}</span>
          </div>
          <button
            onClick={() => onCopy(password || "Demo1234!", "Lösenord")}
            className="text-text-muted hover:text-brand flex-shrink-0"
            data-testid={`admin-demo-copy-password-${d.organization_id}`}
            title="Kopiera lösenord"
          >
            <Copy size={14} />
          </button>
        </div>

        {d.exists && (
          <>
            <div className="grid grid-cols-4 gap-2 pt-2">
              <div className="text-center">
                <div className="text-lg font-heading tabular-nums">{d.users}</div>
                <div className="text-[10px] text-text-muted uppercase tracking-wider">Konton</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-heading tabular-nums">{d.objects}</div>
                <div className="text-[10px] text-text-muted uppercase tracking-wider">Objekt</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-heading tabular-nums">{d.tasks}</div>
                <div className="text-[10px] text-text-muted uppercase tracking-wider">Moment</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-heading tabular-nums">{d.time_reports}</div>
                <div className="text-[10px] text-text-muted uppercase tracking-wider">Rapporter</div>
              </div>
            </div>
            <button
              onClick={openDemo}
              disabled={opening}
              className="w-full mt-3 bg-brand text-white hover:opacity-90 disabled:opacity-50 rounded-md px-3 py-2 text-sm inline-flex items-center justify-center gap-2"
              data-testid={`admin-demo-open-${d.organization_id}`}
            >
              <SignIn size={14} weight="bold" />
              {opening ? "Öppnar…" : "Öppna demo som admin"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function SandboxCleanupPanel({ preview, loading, cleaning, onReload, onApply }) {
  const count = preview?.matched_count ?? null;
  const affected = preview?.affected || [];

  // Kort dölj-panel när det inte finns något att städa
  if (!loading && count === 0) {
    return (
      <div
        className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 mb-6 text-sm text-emerald-800 flex items-center gap-2"
        data-testid="admin-sandbox-clean"
      >
        <Broom size={14} weight="fill" />
        Inga sandbox-referenser kvar i produktions-DB.
        <button
          onClick={onReload}
          className="ml-auto text-xs underline hover:opacity-70"
          data-testid="admin-sandbox-refresh"
        >
          Kolla igen
        </button>
      </div>
    );
  }

  return (
    <div
      className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6"
      data-testid="admin-sandbox-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Broom size={16} weight="fill" className="text-amber-700" />
            <h3 className="font-heading text-base text-amber-900">Sandbox-referenser att städa</h3>
          </div>
          <p className="text-sm text-amber-800 leading-relaxed">
            {loading
              ? "Söker efter orgar med sub_TEST_/cus_TEST_-referenser…"
              : `${count} org(ar) har fantom-Stripe-ID från tidigare tester. Migrera dem till "grandfathered" så de får full access utan Stripe-koppling.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onReload}
            className="border border-amber-300 hover:bg-amber-100 text-amber-800 rounded-md px-3 py-2 text-sm inline-flex items-center gap-2"
            data-testid="admin-sandbox-refresh"
          >
            <ArrowsClockwise size={14} weight="bold" />
            Uppdatera
          </button>
          <button
            onClick={onApply}
            disabled={cleaning || !count}
            className="bg-amber-700 hover:bg-amber-800 text-white disabled:opacity-50 rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 whitespace-nowrap"
            data-testid="admin-sandbox-apply"
          >
            <Broom size={14} weight="fill" className={cleaning ? "animate-pulse" : ""} />
            {cleaning ? "Städar…" : `Städa ${count || ""}`}
          </button>
        </div>
      </div>

      {affected.length > 0 && (
        <div className="border border-amber-200 rounded-md bg-white/60 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-amber-100/80 text-amber-900">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Namn</th>
                <th className="text-left px-3 py-2 font-medium">Slug</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Stripe sub_id</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-200">
              {affected.map((o) => (
                <tr key={o.organization_id} data-testid={`admin-sandbox-row-${o.organization_id}`}>
                  <td className="px-3 py-2">{o.name}</td>
                  <td className="px-3 py-2 text-text-muted">/{o.slug}</td>
                  <td className="px-3 py-2">{o.subscription_status || "—"}</td>
                  <td className="px-3 py-2 font-mono">{o.stripe_subscription_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function InquiryCard({ inquiry: i, savingId, onUpdateStatus }) {
  return (
    <div
      className="bg-surface border border-app rounded-xl p-5"
      data-testid={`admin-inquiry-${i.inquiry_id}`}
    >      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-heading text-lg flex items-center gap-2">
            <Buildings size={16} className="text-brand" /> {i.company}
          </div>
          <div className="text-sm text-text-muted mt-1">
            {i.name}
            {i.team_size && (
              <span className="ml-2 text-xs bg-app border border-app rounded px-1.5 py-0.5">
                {i.team_size} medarbetare
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={
              "text-xs uppercase tracking-widest px-2.5 py-1 rounded-full " +
              (STATUS_STYLE[i.status] || "bg-neutral-100 text-neutral-600")
            }
          >
            {STATUS_LABEL[i.status] || i.status}
          </span>
          <select
            value={i.status}
            onChange={(e) => onUpdateStatus(i.inquiry_id, e.target.value)}
            disabled={savingId === i.inquiry_id}
            className="text-xs border border-app rounded px-2 py-1 bg-app disabled:opacity-50"
            data-testid={`admin-inquiry-status-${i.inquiry_id}`}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-text-muted mb-3">
        <a href={`mailto:${i.email}`} className="hover:text-brand inline-flex items-center gap-1">
          <EnvelopeSimple size={12} /> {i.email}
        </a>
        {i.phone && (
          <a href={`tel:${i.phone}`} className="hover:text-brand inline-flex items-center gap-1">
            <Phone size={12} /> {i.phone}
          </a>
        )}
        <span>System: <strong className="text-text-primary">{i.integration_type}</strong></span>
        <span>{fmtDate(i.created_at)}</span>
      </div>

      <p className="text-sm leading-relaxed bg-app border border-app rounded-md p-3 whitespace-pre-wrap">
        {i.message}
      </p>
    </div>
  );
}

function SignupCard({ signup: s, ownOrgId, onDelete }) {
  const trialLeft = (() => {
    if (!s.trial_ends_at) return null;
    const ms = new Date(s.trial_ends_at).getTime() - Date.now();
    if (ms <= 0) return "utgången";
    const days = Math.ceil(ms / 86_400_000);
    return `${days} dagar kvar`;
  })();

  const isOwnOrg = ownOrgId && s.organization_id === ownOrgId;
  const isActive = s.subscription_status === "active";
  const canDelete = !isOwnOrg && !isActive;

  return (
    <div
      className="bg-surface border border-app rounded-xl p-4 flex flex-wrap items-center gap-4"
      data-testid={`admin-signup-${s.organization_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="font-heading text-base flex items-center gap-2 truncate">
          <Buildings size={14} className="text-brand flex-shrink-0" />
          <span className="truncate">{s.name}</span>
          <span className="text-xs text-text-muted font-normal">/{s.slug}</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-muted mt-1">
          {s.admin_email && (
            <a href={`mailto:${s.admin_email}`} className="hover:text-brand inline-flex items-center gap-1">
              <EnvelopeSimple size={12} /> {s.admin_email}
            </a>
          )}
          <span className="inline-flex items-center gap-1">
            <Users size={12} /> {s.user_count} {s.user_count === 1 ? "användare" : "användare"}
          </span>
          <span className="inline-flex items-center gap-1">
            <CreditCard size={12} /> {s.seats} platser
          </span>
          <span>Registrerad {fmtDateShort(s.created_at)}</span>
          {trialLeft && s.subscription_status === "trialing" && (
            <span className="text-brand font-medium">Trial: {trialLeft}</span>
          )}
        </div>
      </div>
      <span
        className={
          "text-xs uppercase tracking-widest px-2.5 py-1 rounded-full whitespace-nowrap " +
          (SUB_STATUS_STYLE[s.subscription_status] || "bg-neutral-100 text-neutral-600")
        }
        data-testid={`admin-signup-status-${s.organization_id}`}
      >
        {SUB_STATUS_LABEL[s.subscription_status] || s.subscription_status}
      </span>
      <button
        type="button"
        onClick={onDelete}
        disabled={!canDelete}
        title={
          isOwnOrg
            ? "Kan inte radera din egen org"
            : isActive
              ? "Avsluta prenumerationen i Stripe först"
              : "Radera org och all dess data"
        }
        data-testid={`admin-signup-delete-${s.organization_id}`}
        className={
          "flex-shrink-0 p-2 rounded-md transition-colors " +
          (canDelete
            ? "text-red-600 hover:bg-red-50 cursor-pointer"
            : "text-neutral-300 cursor-not-allowed")
        }
      >
        <Trash size={18} />
      </button>
    </div>
  );
}

function PurgeTrialsPanel({
  days, onDaysChange, preview, loading, applying, onLoadPreview, onApply, onCancel,
}) {
  return (
    <div className="bg-surface border border-app rounded-xl p-5 mb-6" data-testid="admin-purge-trials-panel">
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 mb-1">
            <Broom size={18} className="text-brand" />
            <h2 className="font-heading text-lg">Rensa avbrutna trial-orgar</h2>
          </div>
          <p className="text-sm text-text-muted leading-relaxed">
            Radera permanent alla orgar som är <em>pending, trialing eller canceled</em> och äldre än N dagar. Egen org och betalande kunder skyddas alltid. Kör alltid preview först.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-[10px] text-text-muted uppercase mb-1">Ålder (dagar)</label>
            <input
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => onDaysChange(Math.max(1, Math.min(365, Number(e.target.value) || 30)))}
              className="tk-input w-24"
              data-testid="admin-purge-days-input"
            />
          </div>
          <button
            onClick={onLoadPreview}
            disabled={loading || applying}
            className="tk-btn-outline text-sm py-2 px-4 whitespace-nowrap"
            data-testid="admin-purge-preview-button"
          >
            {loading ? "Laddar…" : "Visa preview"}
          </button>
        </div>
      </div>

      {preview && (
        <div className="mt-5 border-t border-app pt-4">
          {preview.count === 0 ? (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-center gap-2">
              Inga avbrutna trial-orgar äldre än {days} dagar. Allt är rent.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3">
                <Warning size={16} className="text-red-600 flex-shrink-0" />
                <div className="text-sm">
                  <strong>{preview.count}</strong> org{preview.count === 1 ? "" : "ar"} kommer att raderas permanent
                </div>
              </div>
              <ul className="max-h-60 overflow-y-auto space-y-1 text-xs mb-4" data-testid="admin-purge-preview-list">
                {preview.candidates.map((c) => (
                  <li
                    key={c.organization_id}
                    className="flex items-center justify-between border border-app rounded px-2 py-1.5"
                  >
                    <span className="truncate">
                      <strong className="text-text-primary">{c.name}</strong>
                      <span className="text-text-muted ml-2">
                        {SUB_STATUS_LABEL[c.subscription_status] || c.subscription_status} · {c.age_days} d gammal
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={onApply}
                  disabled={applying}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-md px-4 py-2 text-sm inline-flex items-center gap-2"
                  data-testid="admin-purge-apply-button"
                >
                  <Trash size={14} />
                  {applying ? "Raderar…" : `Radera ${preview.count} orgar`}
                </button>
                <button
                  onClick={onCancel}
                  disabled={applying}
                  className="tk-btn-outline text-sm py-2 px-4"
                >
                  Avbryt
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
